import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ShoppingBag, ChevronDown } from 'lucide-react';
import Logo from './Logo';
import { NAV } from '../mock';

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openGroup, setOpenGroup] = useState(null);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener('scroll', onScroll);
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const solid = scrolled || mobileOpen || pathname.startsWith('/admin') || pathname.startsWith('/unsubscribe');
  const variant = solid ? 'blue' : 'white';
  const linkColor = solid ? 'text-[#3a6d8f]' : 'text-white';

  return (
    <header
      className={`print:hidden fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        solid ? 'bg-white/95 backdrop-blur shadow-[0_2px_24px_rgba(0,0,0,0.08)] py-2' : 'bg-transparent py-4'
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10 flex items-center justify-between">
        <Link to="/" className="shrink-0">
          <Logo variant={variant} />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((item) => (
            <div key={item.label} className="relative group">
              <Link
                to={item.href}
                className={`font-sans-ah tracking-nav text-[12px] font-500 flex items-center gap-1 ${linkColor} link-underline`}
              >
                {item.label}
                {item.children && <ChevronDown size={13} strokeWidth={2} />}
              </Link>
              {item.children && (
                <div className="absolute left-0 top-full pt-4 opacity-0 invisible translate-y-2 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-300">
                  <div className="bg-white shadow-xl rounded-sm py-2 min-w-[220px] border-t-2 border-[#3d7ca6]">
                    {item.children.map((c) => (
                      <Link
                        key={c.label}
                        to={c.href}
                        className="block px-5 py-2.5 font-sans-ah text-[13px] tracking-wide-ah text-[#2f2f2f] hover:bg-[#f0ece4] hover:text-[#3d7ca6] transition-colors"
                      >
                        {c.label}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
          <button className={`flex items-center gap-1.5 ${linkColor}`} aria-label="Cart">
            <ShoppingBag size={19} strokeWidth={1.6} />
            <span className="font-sans-ah text-[12px]">0</span>
          </button>
        </nav>

        {/* Mobile toggle */}
        <button
          className={`lg:hidden ${solid ? 'text-[#2c5f80]' : 'text-white'}`}
          onClick={() => setMobileOpen((v) => !v)}
          aria-label="Menu"
        >
          {mobileOpen ? <X size={28} /> : <Menu size={28} />}
        </button>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-neutral-100 max-h-[80vh] overflow-y-auto">
          <nav className="px-6 py-4 flex flex-col">
            {NAV.map((item) => (
              <div key={item.label} className="border-b border-neutral-100">
                <button
                  className="w-full flex items-center justify-between py-3.5 font-sans-ah tracking-nav text-[13px] text-[#2c5f80]"
                  onClick={() =>
                    item.children ? setOpenGroup(openGroup === item.label ? null : item.label) : setMobileOpen(false)
                  }
                >
                  {item.children ? (
                    <>
                      {item.label}
                      <ChevronDown
                        size={16}
                        className={`transition-transform ${openGroup === item.label ? 'rotate-180' : ''}`}
                      />
                    </>
                  ) : (
                    <Link to={item.href} onClick={() => setMobileOpen(false)}>
                      {item.label}
                    </Link>
                  )}
                </button>
                {item.children && openGroup === item.label && (
                  <div className="pb-2 pl-3">
                    {item.children.map((c) => (
                      <Link
                        key={c.label}
                        to={c.href}
                        onClick={() => setMobileOpen(false)}
                        className="block py-2 font-sans-ah text-[13px] text-[#555]"
                      >
                        {c.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}
